import {ChangeDetectorRef, Component, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {NgbModal, NgbPagination} from '@ng-bootstrap/ng-bootstrap';
import {ThaiDateTimePipe} from '../../../shared/commons/thai-datetime.pipe';

import {firstValueFrom} from 'rxjs';
import {BanDto, BanWordDto, Level, StandardCodeDto, StandardCodeId} from '../../../../generated-model/models';
import {BanService, BanWordLevel} from './ban-service';
import {BanModal} from './ban-modal';
import {ConfirmDialogService} from '../../../shared/ui/confirm-dialog/confirm-dialog.service';

type BanViewDto = Omit<Partial<BanDto>, 'banWords'> & { banWords?: Array<Partial<BanWordDto>> };

import {resolveTotalElements} from '../../../shared/commons/pagination.util';
import {PageTitleComponent} from '../../../shared/commons/page-title/page-title.component';
import {PagesizeSelectComponent} from '../../../shared/components/pagesize-select/pagesize-select.component';
import {StatusBadgeComponent} from '../../../shared/components/status-badge/status-badge.component';
import {SearchIconComponent} from '../../../shared/components/search-icon/search-icon.component';

@Component({
  selector: 'app-ban',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbPagination, ThaiDateTimePipe, PageTitleComponent, PagesizeSelectComponent, StatusBadgeComponent, SearchIconComponent],
  templateUrl: './ban.html',
  styleUrl: './ban.scss'
})
export class BanComponent implements OnInit {
  private readonly service = inject(BanService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalService = inject(NgbModal);
  private readonly confirmDialogService = inject(ConfirmDialogService);

  protected readonly standardCodeQuery = signal('');
  protected readonly levelFilter = signal<BanWordLevel | ''>('');
  protected readonly stateFilter = signal<number | null>(null);
  protected readonly isLoading = signal(false);
  protected readonly page = signal(1);
  protected readonly pageSize = signal(10);
  protected readonly collectionSize = signal(0);
  protected readonly displayedItems = signal<BanViewDto[]>([]);
  protected readonly standardCodes = signal<StandardCodeDto[]>([]);

  readonly majorLevel: BanWordLevel = Level.major;
  readonly minorLevel: BanWordLevel = Level.minor;

  private totalElements = 0;

  constructor() {
  }

  ngOnInit(): void {
    this.loadStandardCodes();
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.service.getAllBans(this.page() - 1, this.pageSize(), this.levelFilter(), this.standardCodeQuery(), this.stateFilter()).subscribe({
      next: (data) => {
        this.displayedItems.set(data.content);
        this.totalElements = resolveTotalElements(data);
        this.collectionSize.set(this.totalElements);
        this.isLoading.set(false);
        this.cdr.detectChanges();
      },
      error: () => {
        this.isLoading.set(false);
        this.cdr.detectChanges();
      }
    });
  }

  loadStandardCodes(): void {
    this.service.getActiveStandardCodes().subscribe({
      next: (data) => {
        this.standardCodes.set(data);
        this.cdr.detectChanges();
      },
      error: () => {
        this.standardCodes.set([]);
        this.cdr.detectChanges();
      }
    });
  }

  onSearchChange(): void {
    this.page.set(1);
    this.load();
  }

  clearSearch(): void {
    this.standardCodeQuery.set('');
    this.levelFilter.set('');
    this.stateFilter.set(null);
    this.onSearchChange();
  }

  openAddModal(): void {
    const modalRef = this.modalService.open(BanModal, {size: 'xl'});
    modalRef.componentInstance.standardCodes = this.standardCodes();
    modalRef.componentInstance.onSave = async (result: BanViewDto): Promise<boolean> => {
      const confirmed = await this.confirmDialogService.confirmSave();
      if (!confirmed) {
        return false;
      }
      try {
        await firstValueFrom(this.service.createBan(result as BanDto));
        this.load();
        return true;
      } catch {
        return false;
      }
    };
  }

  openEditModal(item: BanViewDto): void {
    const modalRef = this.modalService.open(BanModal, {size: 'xl'});
    modalRef.componentInstance.ban = item;
    modalRef.componentInstance.standardCodes = this.standardCodes();
    modalRef.componentInstance.onSave = async (result: BanViewDto): Promise<boolean> => {
      const confirmed = await this.confirmDialogService.confirmSave();
      if (!confirmed || !result.id) {
        return false;
      }
      try {
        await firstValueFrom(this.service.updateBan(result.id, result as BanDto));
        this.load();
        return true;
      } catch {
        return false;
      }
    };
  }

  openViewModal(item: BanViewDto): void {
    const modalRef = this.modalService.open(BanModal, {size: 'xl'});
    modalRef.componentInstance.ban = item;
    modalRef.componentInstance.standardCodes = this.standardCodes();
    modalRef.componentInstance.viewOnly = true;
  }

  async delete(id: number): Promise<void> {
    const confirmed = await this.confirmDialogService.confirmDelete('คุณต้องการลบข้อมูลนี้ใช่หรือไม่?');
    if (!confirmed) {
      return;
    }
    this.service.deleteBan(id).subscribe(() => this.load());
  }

  getStandardCodeLabel(standardCodeId?: StandardCodeId): string {
    if (!standardCodeId) {
      return '-';
    }
    const codes = this.standardCodes();
    const idValue = typeof standardCodeId === 'object' ? (standardCodeId as any).value ?? standardCodeId : standardCodeId;
    const item = codes.find((code) => {
      const codeIdValue = typeof code.id === 'object' ? (code.id as any).value ?? code.id : code.id;
      return codeIdValue === idValue;
    });
    if (!item) {
      return String(idValue);
    }
    const preCode = item.preCode?.trim() ?? '';
    const standardCode = item.standardCode?.trim() ?? '';
    const title = item.title?.trim() ?? '';
    return `${preCode} ${standardCode} ${title}`.trim();
  }

  countWords(item: BanViewDto, level: BanWordLevel): number {
    return (item.banWords ?? []).filter((word) => word.level === level).length;
  }
}
